/** @jsxImportSource @emotion/react */
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CSSObject } from '@emotion/react';
import Title from '../../components/title';
import Clickable from '../../components/clickable';
import LocalImage from '../../components/localImage';
import useHttpCall from '../../hooks/useHttpCall';
import useTheme from '../../hooks/useTheme';
import useLanguage from '../../hooks/useLanguage';
import RequestError from '../../utils/requestError';
import environment from '../../utils/environment';
import { AppContext } from '../../contexts/appContext';
import ResetPasswordView from './resetPasswordView';

// Creating a link from a phone number also sends the email server-side,
// so nothing about the link or the recipient comes back to the client.
interface IMagicLinkSendResponse {
  result: string;
}

interface IMagicLinkConvertResponse {
  sessionId: string;
  deviceId: string;
}

interface ILocationState {
  countryCode?: string;
  phoneNumber?: string;
}

const MagicLinkView = () => {
  const handleHttpCall = useHttpCall();
  const location = useLocation<ILocationState>();
  const history = useHistory();
  const { saveSession, logout } = useContext(AppContext);
  const { color } = useTheme();
  const language = useLanguage();

  const token = new URLSearchParams(location.search).get('token');

  const [convertError, setConvertError] = useState<string | null>(null);
  const [showResetPassword, setShowResetPassword] = useState(false);

  // A token is single-use, so it must be exchanged exactly once. This effect
  // depends on `saveSession`, whose identity changes on every render, so
  // without this guard a re-render would retry the spent token, get a 401 and
  // trip the logout interceptor — bouncing the patient out to the login page.
  const convertedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token || convertedTokenRef.current === token) {
      return;
    }

    convertedTokenRef.current = token;

    handleHttpCall<IMagicLinkConvertResponse>({
      method: 'POST',
      url: '/api/user/fr/magic-link/convert',
      body: { token },
    })
      .then((response) => {
        // French patients redefine their password before entering the app.
        if (environment.COUNTRY === 'FR') {
          saveSession(response.sessionId, response.deviceId);
          setShowResetPassword(true);
        } else {
          saveSession(response.sessionId, response.deviceId, true);
        }
      })
      .catch((err: RequestError) => {
        setConvertError(err.message || 'This link is invalid or has expired.');
      });
  }, [token, handleHttpCall, saveSession]);

  // The phone number the patient just verified via SMS, forwarded from the
  // password screen. The backend looks up the account's email from it.
  const patientCountryCodeRef = useRef(location.state?.countryCode ?? '');
  const patientPhoneNumberRef = useRef(location.state?.phoneNumber ?? '');

  const [isAutoSending, setIsAutoSending] = useState(false);
  const [autoSendError, setAutoSendError] = useState<string | null>(null);

  const sendPatientMagicLinkEmail = useCallback(() => {
    const countryCode = patientCountryCodeRef.current;
    const phoneNumber = patientPhoneNumberRef.current;

    if (!countryCode || !phoneNumber) {
      return;
    }

    setIsAutoSending(true);
    setAutoSendError(null);

    handleHttpCall<IMagicLinkSendResponse>({
      method: 'POST',
      url: '/api/user/fr/magic-link/create',
      body: {
        countryCode,
        phoneNumber,
        partner: environment.IS_KRY_APP ? 'KRY' : 'LIVI',
      },
    })
      .catch((err: RequestError) => {
        setAutoSendError(
          err.message || "Une erreur est survenue lors de l'envoi de l'e-mail."
        );
      })
      .finally(() => {
        setIsAutoSending(false);
      });
  }, [handleHttpCall]);

  useEffect(() => {
    if (token) {
      return;
    }

    sendPatientMagicLinkEmail();
  }, [token, sendPatientMagicLinkEmail]);

  const SECTION_STYLE: CSSObject = {
    marginBottom: 32,
  };

  if (token) {
    if (showResetPassword) {
      // Tear down the magic link session so the patient signs in again with
      // their new password. Forced because appStart data was never refetched
      // for this session, so the client still believes nobody is logged in.
      return (
        <ResetPasswordView onSuccess={() => logout(false, { force: true })} />
      );
    }

    return (
      <Fragment>
        <div css={{ padding: '0 20px 40px 20px' }}>
          <section css={SECTION_STYLE}>
            <Title title='Logging you in…' />
          </section>

          {convertError && (
            <section css={SECTION_STYLE}>
              <div css={{ color: 'red' }}>{convertError}</div>
            </section>
          )}
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div css={{ padding: '20px 20px 40px 20px' }}>
        <Clickable
          dim
          onClick={() => history.goBack()}
          aria-label={language.get('back')}
          css={{
            display: 'inline-flex',
            alignItems: 'center',
            color: color.PRIMARY,
            marginBottom: 24,
          }}
        >
          <LocalImage
            src='CHEVRON_LEFT'
            tint='PRIMARY'
            alt=''
            css={{ minWidth: 24 }}
          />
          {language.get('back')}
        </Clickable>

        <Title title='Vérifiez vos e-mails' large />

        <div css={{ marginTop: 24 }}>
          <p>
            Nous avons envoyé un lien de réinitialisation à votre adresse
            e-mail. Ce lien est valide 1 heure.
          </p>

          <p css={{ marginTop: 16 }}>
            Vous n&apos;avez pas reçu de lien ?
            <br />
            Vérifiez vos spams ou{' '}
            {/* renderAs='span': a <button>'s contents are one unbreakable
                inline box that can't wrap with surrounding text, which is
                what was forcing this whole phrase onto its own line. A span
                flows normally. Spans can't be natively disabled, so the
                click-guard and visual/AT affordances are done by hand. */}
            <Clickable
              renderAs='span'
              onClick={() => {
                if (!isAutoSending) {
                  sendPatientMagicLinkEmail();
                }
              }}
              aria-disabled={isAutoSending}
              css={{
                display: 'inline',
                color: color.PRIMARY,
                fontWeight: 700,
                textDecoration: 'underline',
                ...(isAutoSending && { pointerEvents: 'none', opacity: 0.6 }),
              }}
            >
              cliquez ici pour renvoyer le lien
            </Clickable>
            .
          </p>

          {autoSendError && (
            <p css={{ color: 'red', marginTop: 16 }}>{autoSendError}</p>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default MagicLinkView;
